import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Navbar from "../../components/navbar";
import { getPatientEvaluation, getPatientHome } from "redux/actions/patients";
import { parseDateFromDatetime } from "utils/dataAndTime";

import {
  getRofServices,
  ensureSdkPatientAndAssessment,
  linkEvaluationToRof,
  syncSdkPatientDemographics,
  calcAge,
  mapGenderAtBirthForSdk as mapGenderAtBirth,
  parseHeightCm,
} from "utils/rofClient";
import { RiskOfFallApp } from "@kinetikos/rof";
import bigBackButton from "../../assets/images/big-left-arrow.svg";
import "@kinetikos/rof/dist/index.css";


const RofAssessment = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { patient_id, evaluation_id } = useParams();
  const location = useLocation();
  const rofLinkFromState = location.state?.rofLink || null;

  const currEvaluation = useSelector(
    (s) => s.patientEvaluations.currEvaluation
  );
  const patientHome = useSelector((s) => s.patientHome);

  const [services, setServices] = useState(null);
  const [booting, setBooting] = useState(true);
  const [bootErr, setBootErr] = useState(null);

  const [sdkPatientId, setSdkPatientId] = useState(null);
  const [sdkAssessmentId, setSdkAssessmentId] = useState(null);

  // Refs to read Redux data inside the bootstrap effect without
  // making them dependencies (they change frequently / circularly).
  const patientHomeRef = useRef(patientHome);
  const currEvaluationRef = useRef(currEvaluation);
  const bootstrappedEvalRef = useRef(null);

  useEffect(() => { patientHomeRef.current = patientHome; }, [patientHome]);
  useEffect(() => { currEvaluationRef.current = currEvaluation; }, [currEvaluation]);

  useEffect(() => {
    if (!patient_id) return;
    dispatch(getPatientHome(patient_id));
    if (evaluation_id)
      dispatch(getPatientEvaluation(patient_id, evaluation_id));
  }, [dispatch, patient_id, evaluation_id]);

  const patientSeed = useMemo(() => {
    const gender_at_birth = mapGenderAtBirth(patientHome?.gender);
    const height_cm = parseHeightCm(patientHome);
    return {
      age: calcAge(patientHome?.birthdate),
      gender_at_birth,
      height_cm,
      hasMin: !!gender_at_birth && !!height_cm,
    };
  }, [
    patientHome?.gender,
    patientHome?.birthdate,
    patientHome?.height_cm,
    patientHome?.height,
  ]);

  // Bootstrap SDK services and ensure we have patient+assessment created & linked.
  //
  // Dependencies are deliberately kept to stable/primitive values only.
  // patientHome and currEvaluation are read via refs so their object-reference
  // changes don't re-trigger this effect and cause the rapid-swap flicker.
  useEffect(() => {
    // Already bootstrapped for this evaluation — skip.
    if (bootstrappedEvalRef.current === evaluation_id) return;

    let cancelled = false;

    (async () => {
      try {
        setBootErr(null);
        setBooting(true);

        const s = await getRofServices({ contentLanguage: i18n.language });
        if (cancelled) return;
        setServices(s);

        // Read current Redux state from refs (avoids dep-cycle).
        const evalDetails = currEvaluationRef.current?.evaluation_details;
        const pHome = patientHomeRef.current;

        // Only trust currEvaluation data if it matches the current evaluation_id.
        // On navigation, the ref may still hold the previous evaluation's data.
        const evalMatchesCurrent =
          evalDetails &&
          (String(evalDetails.id) === String(evaluation_id) ||
           String(evalDetails.evaluation_id) === String(evaluation_id));

        const mappedEvalPatient = evalMatchesCurrent
          ? (evalDetails?.rof_sdk_patient_id ?? null)
          : null;
        const mappedEvalAssessment = evalMatchesCurrent
          ? (evalDetails?.rof_sdk_assessment_id ?? null)
          : null;

        // rofLinkFromState (passed at navigation time) takes priority —
        // it's always for the current evaluation, unlike stale Redux data.
        const mappedPatient =
          rofLinkFromState?.sdk_patient_id ??
          mappedEvalPatient ??
          pHome?.rof_sdk_patient_id ??
          null;

        let finalPatientId = mappedPatient;
        let finalAssessmentId =
          rofLinkFromState?.sdk_assessment_id ??
          mappedEvalAssessment ??
          null;

        if (!mappedPatient || !mappedEvalAssessment) {
          if (patientSeed.hasMin) {
            const out = await ensureSdkPatientAndAssessment({
              sdkPatientId: mappedPatient,
              sdkAssessmentId: mappedEvalAssessment,
              patientPayload: {
                age: patientSeed.age,
                gender_at_birth: patientSeed.gender_at_birth,
                height_cm: patientSeed.height_cm,
              },
            });

            finalPatientId = out.sdkPatientId ?? finalPatientId;
            finalAssessmentId = out.sdkAssessmentId ?? finalAssessmentId;

            await linkEvaluationToRof(evaluation_id, {
              sdkPatientId: finalPatientId ?? null,
              sdkAssessmentId: finalAssessmentId ?? null,
            });
          }
        }

        // Sync SDK patient demographics with platform data (fire-and-forget).
        if (finalPatientId && pHome) {
          syncSdkPatientDemographics({
            sdkPatientId: finalPatientId,
            platformPatient: pHome,
          });
        }

        if (!cancelled) {
          bootstrappedEvalRef.current = evaluation_id;
          setSdkPatientId(finalPatientId ?? null);
          setSdkAssessmentId(finalAssessmentId ?? null);
        }
      } catch (e) {
        if (!cancelled) setBootErr(e?.message || "Bootstrap failed");
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    i18n.language,
    evaluation_id,
    patientSeed.hasMin,
    patientSeed.age,
    patientSeed.gender_at_birth,
    patientSeed.height_cm,
    rofLinkFromState?.sdk_patient_id,
    rofLinkFromState?.sdk_assessment_id,
  ]);

  // Keep backend mapping in sync if the user creates data inside the SDK
  const handlePatientCreated = async (p) => {
    try {
      setSdkPatientId(p?.id ?? null);
      await linkEvaluationToRof(evaluation_id, {
        sdkPatientId: p?.id ?? null,
        sdkAssessmentId: sdkAssessmentId ?? null,
      });
    } catch (e) {
      console.warn("Failed to link patient after SDK creation", e);
    }
  };

  const handlePatientSelected = (p) => {
    setSdkPatientId(p?.id ?? null);
  };

  const handleAssessmentCreated = async (a) => {
    try {
      setSdkAssessmentId(a?.id ?? null);
      await linkEvaluationToRof(evaluation_id, {
        sdkPatientId: a?.patient ?? sdkPatientId ?? null,
        sdkAssessmentId: a?.id ?? null,
      });
    } catch (e) {
      console.warn("Failed to link assessment after SDK creation", e);
    }
  };

  const handleAssessmentSelected = (a) => {
    setSdkAssessmentId(a?.id ?? null);
  };

  const goBack = () =>
    props.history.replace(`/patient/${patient_id}/evaluations`);

  const createdAt = currEvaluation?.evaluation_details?.date
    ? parseDateFromDatetime(currEvaluation.evaluation_details.date)
    : "—";

  return (
    <div className="main-wrapper">
      <Navbar blueVersion={true} />

      <div className="bg-white border-bottom">
        <div className="container-xl py-3">
          <div className="d-flex align-items-center mb-2">
            <img
              src={bigBackButton}
              alt={t("buttons.back", "Back")}
              onClick={goBack}
              className="mr-2"
              style={{ cursor: "pointer" }}
            />
            <h4 className="m-0">
              {t("rof.title", "Risk of Fall (Video Assessment)")}
            </h4>
          </div>
          <div className="mt-2">
            <div className="font-weight-medium">
              {t("rof.patient_name", "Patient Name")}:{" "}
              <span className="text-dark">{patientHome?.name ?? "—"}</span>
            </div>
            <div className="text-muted">
              {t("rof.created_on", "Created on")}:{" "}
              <span className="text-dark">{createdAt}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xl py-3">
        {booting ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border mr-2" role="status" />
            <span>{t("rof.loading")}</span>
          </div>
        ) : bootErr ? (
          <div className="alert alert-danger d-flex align-items-center">
            <span className="mr-2">⚠️</span>
            <div>{bootErr}</div>
          </div>
        ) : services ? (
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <RiskOfFallApp
                services={services}
                patientId={sdkPatientId ?? undefined}
                assessmentId={sdkAssessmentId ?? undefined}
                onPatientCreated={handlePatientCreated}
                onPatientSelected={handlePatientSelected}
                onAssessmentCreated={handleAssessmentCreated}
                onAssessmentSelected={handleAssessmentSelected}
              />
            </div>
          </div>
        ) : (
          <div className="alert alert-warning mb-0">
            {t("errors.generic", "Something went wrong. Please try again.")}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(RofAssessment);
